/** Files on an applicant, FOIA request, desk report or safety concern: list and upload (with an optional item / step tag). */
import { useRef, useState } from 'react';
import { api } from '../shared/api.ts';
import { ErrorBox, useAction } from '../shared/ui.tsx';
import { fileBase64, when } from './common.tsx';

export type WorkFile = { id: string; item: string | null; name: string; mime: string; size: number; uploaded_at: string; by_name: string };
const size = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function FileList({ files, itemLabel }: { files: WorkFile[]; itemLabel?: (item: string) => string }) {
  if (!files.length) return <p className="muted small">No files yet.</p>;
  return (
    <ul className="plain file-list">
      {files.map((f) => (
        <li key={f.id}>
          {f.mime.startsWith('image/') && <img className="wf-thumb" src={`/api/work-files/${f.id}`} alt="" />}
          <a href={`/api/work-files/${f.id}`} target="_blank" rel="noreferrer">
            {f.name}
          </a>
          <span className="small muted">
            {f.item && itemLabel ? `${itemLabel(f.item)} · ` : ''}
            {size(f.size)} · {f.by_name} · {when(f.uploaded_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** "Add files" / "Take a photo" button that uploads to `url` and calls onDone. */
export function UploadButton({ url, item, onDone, label = 'Add files', accept, camera, small = true }: { url: string; item?: string | null; onDone: () => void; label?: string; accept?: string; camera?: boolean; small?: boolean }) {
  const pick = useRef<HTMLInputElement>(null);
  const a = useAction();
  const [n, setN] = useState(0);
  return (
    <>
      <input
        ref={pick}
        type="file"
        multiple={!camera}
        hidden
        accept={accept}
        capture={camera ? 'environment' : undefined}
        aria-label={label}
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          a.run(async () => {
            for (const [i, f] of fs.entries()) {
              setN(i + 1);
              await api.post(url, { name: f.name, dataBase64: await fileBase64(f), item: item ?? undefined });
            }
            if (pick.current) pick.current.value = '';
            onDone();
          });
        }}
      />
      <button type="button" className={`btn ${small ? 'sm' : ''}`} disabled={a.busy} onClick={() => pick.current?.click()}>
        {a.busy ? `Uploading ${n}…` : label}
      </button>
      {a.error ? <ErrorBox error={a.error} /> : null}
    </>
  );
}
